/**
 * ShareRecipientsListContainer - Maps share data into display props
 *
 * Transforms raw share recipient objects (from ScoutReportShare or NoteShare)
 * into display-ready data by extracting Cognito display names and computing
 * initials. Supports appending pre-mapped additional recipients (e.g., managers
 * with implicit role-based access). Designed to be reusable across report and
 * note sharing contexts.
 */

import { useMemo } from "react";

import ShareRecipientsListView, {
  RecipientDisplayData,
} from "./ShareRecipientsListView";

import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";

/** Shape of a single share recipient from the GraphQL layer */
export interface ShareRecipient {
  readonly userId: string;
  readonly role: string;
  readonly createdAt: string;
  readonly user: {
    readonly id: string;
    readonly cognitoUser?: {
      readonly Username?: string | null;
      readonly Attributes?:
        | readonly { readonly Name: string; readonly Value: string }[]
        | null;
      readonly UserAttributes?:
        | readonly { readonly Name: string; readonly Value: string }[]
        | null;
    } | null;
  };
}

/** Props for ShareRecipientsListContainer */
interface ShareRecipientsListContainerProps {
  /** Array of share recipient objects from the GraphQL query */
  readonly shares: readonly ShareRecipient[];
  /** Pre-mapped display recipients to append (e.g., managers with implicit access) */
  readonly additionalRecipients?: readonly RecipientDisplayData[];
  /** Callback to revoke a user's access */
  readonly onRevoke: (userId: string) => void;
  /** Whether a revoke mutation is in progress */
  readonly isRevoking: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
}

/**
 * Extracts initials from a display name (first letter of first and last name)
 * @param displayName - The full display name
 */
const getInitials = (displayName: string): string => {
  const parts = displayName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
};

/**
 * Maps a share recipient into display data by checking both Cognito
 * Attributes and UserAttributes for name resolution, falling back to Username
 * @param share - A single share recipient object
 */
const mapShareToDisplayData = (share: ShareRecipient): RecipientDisplayData => {
  const attributes =
    share.user.cognitoUser?.Attributes ??
    share.user.cognitoUser?.UserAttributes ??
    null;
  const displayName = getCognitoDisplayName(
    attributes,
    share.user.cognitoUser?.Username ?? "Unknown"
  );

  return {
    userId: share.userId,
    displayName,
    initials: getInitials(displayName),
    role: share.role,
  };
};

/**
 * Container that transforms share recipient data into display props
 * @param props - Component props
 * @param props.shares - Array of share recipient objects from the GraphQL query
 * @param props.additionalRecipients - Pre-mapped display recipients to append (e.g., managers with implicit access)
 * @param props.onRevoke - Callback to revoke a user's access
 * @param props.isRevoking - Whether a revoke mutation is in progress
 * @param props.isDark - Whether dark mode is active
 */
const ShareRecipientsListContainer = ({
  shares,
  additionalRecipients,
  onRevoke,
  isRevoking,
  isDark,
}: ShareRecipientsListContainerProps) => {
  const recipients = useMemo(
    () => [
      ...shares.map(mapShareToDisplayData),
      ...(additionalRecipients ?? []),
    ],
    [shares, additionalRecipients]
  );

  const revokeHandlers = useMemo(
    () =>
      shares.reduce(
        (acc, share) => ({
          ...acc,
          [share.userId]: () => onRevoke(share.userId),
        }),
        {} as Record<string, () => void>
      ),
    [shares, onRevoke]
  );

  return (
    <ShareRecipientsListView
      recipients={recipients}
      isRevoking={isRevoking}
      isDark={isDark}
      revokeHandlers={revokeHandlers}
    />
  );
};

ShareRecipientsListContainer.displayName = "ShareRecipientsListContainer";

export default ShareRecipientsListContainer;
